import React from 'react'

import { Divider, Badge } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  array, func, object, number, bool, oneOfType, string,
} from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import modelDetails from './Static/modelDetails'
import modelTypes from './Static/modelTypes'

function ModelDetailsTable({
  tags = EMPTY_ARRAY,
  currentSelectedFileTypes,
  setSelectedFilters,
  trackFilterChange,
  types = EMPTY_OBJECT,
  polygons,
  vertices,
  textures = false,
  materials = false,
  uvwMapping = false,
  pluginsUsed = false,
  locale = 'en',
}) {
  // The i18next singleton stays on lng 'en' (non-EN rails bundles are only registered, never
  // activated), so every call must name the page locale explicitly or the panel renders English
  // on /de, /fr, ... - same contract as the gallery's download tooltip.
  const t = (key, options) => i18n.t(key, { ns: 'rails', lng: locale, ...options })

  const details = {
    textures,
    materials,
    uvwMapping,
    pluginsUsed,
  }

  function triggerFiltersChange(isCurrentlySelected, id, selectedLabel) {
    if (isCurrentlySelected) {
      setSelectedFilters({
        name: 'fileTypes',
        value: currentSelectedFileTypes.filter((item) => item !== `${id}`),
      })
    } else {
      trackFilterChange({
        name: 'fileTypes',
        value: selectedLabel,
        eventSource: 'model preview',
      })
      setSelectedFilters({
        name: 'fileTypes',
        value: [...currentSelectedFileTypes, `${id}`],
      })
    }
  }

  const badges = tags
    .filter(({ name }) => !!name)
    .map(({ name, id }) => {
      const isSelected = currentSelectedFileTypes.includes(`${id}`)
      const props = isSelected ? {
        type: 'primary',
      } : {
        type: 'transparent',
        hasBorder: true,
      }

      const selectedLabel = name.replace(/\./g, '')

      return ({
        isSelected,
        value: selectedLabel,
        ...props,
        onClick: () => triggerFiltersChange(isSelected, id, selectedLabel),
      })
    })
    .sort((a, b) => b.isSelected - a.isSelected)

  function renderFileFormatTags() {
    return badges.map(({
      hasBorder, onClick, type, value,
    }) => (
      <Badge
        className="model-details-table__format-badge"
        hasBorder={hasBorder}
        key={value}
        onClick={onClick}
        type={type}
      >
        {value}
      </Badge>
    ))
  }

  function renderAttributes(list, modelValues, keyspace) {
    return list.map(({ key, labelKey }) => (
      <Badge
        key={key}
        type={modelValues[key] ? 'light-green' : 'light-red'}
      >
        {t(`listingPage.gallery.${keyspace}.${labelKey}`)}
      </Badge>
    ))
  }

  function getDetailValue(detail) {
    if (!detail || detail === 'false') {
      return t('listingPage.gallery.unknown')
    }

    return detail
  }

  return (
    <div className="model-details-table cgt-effect-card-border-disabled">
      <div className="model-details-table__row model-details-table__row--with-no-margin">
        <span className="model-details-table__title cgt-legacy-text-tiny">{t('listingPage.gallery.formats')}</span>
        <div className="model-details-table__badges-container">
          {renderFileFormatTags()}
        </div>
      </div>
      <div className="model-details-table__row">
        <span className="model-details-table__title cgt-legacy-text-tiny">{t('listingPage.gallery.geometry')}</span>
        <div className="model-details-table__badges-container">
          <Badge
            type="transparent"
            className="model-details-table__tag"
            hasBorder
          >
            {t('listingPage.gallery.polygons', { value: getDetailValue(polygons) })}
          </Badge>
          <Badge
            type="transparent"
            className="model-details-table__tag"
            hasBorder
          >
            {t('listingPage.gallery.vertices', { value: getDetailValue(vertices) })}
          </Badge>
        </div>
      </div>
      <Divider customClass="model-details-table__divider" />
      <div className="model-details-table__row model-details-table__row--with-no-margin">
        <span className="model-details-table__title cgt-legacy-text-tiny">{t('listingPage.gallery.type')}</span>
        <div className="model-details-table__badges-container">
          {renderAttributes(modelTypes, types, 'types')}
        </div>
      </div>
      <div className="model-details-table__row">
        <span className="model-details-table__title cgt-legacy-text-tiny">{t('listingPage.gallery.details')}</span>
        <div className="model-details-table__badges-container">
          {renderAttributes(modelDetails, details, 'attributes')}
        </div>
      </div>
    </div>
  )
}

ModelDetailsTable.propTypes = {
  tags: array,
  types: object,
  currentSelectedFileTypes: array.isRequired,
  setSelectedFilters: func.isRequired,
  trackFilterChange: func.isRequired,
  polygons: oneOfType([number, string, bool]),
  vertices: oneOfType([number, string, bool]),
  textures: bool,
  materials: bool,
  uvwMapping: bool,
  pluginsUsed: bool,
  locale: string,
}

const mapStateToProps = (context) => ({ locale: context?.locale })

export default WithTriangleUiContext(mapStateToProps)(ModelDetailsTable)
